import { useEffect, useMemo, useState } from 'react';
import { UserTypes } from '$types/UserTypes';
import getUserType from '$utils/getUserType';

interface DefaultIcon2Props {
  firstName: string | undefined;
  lastName: string | undefined;
  picture?: string | undefined;
  addPadding?: boolean | string | undefined;
  boldText?: boolean | undefined;
  fontSize?: string; // uses tailwind text sizes
  hideBorder?: boolean;
}

const DefaultIcon2 = ({
  firstName,
  lastName,
  picture,
  addPadding,
  boldText,
  fontSize,
  hideBorder,
}: DefaultIcon2Props) => {
  const [userFirstName, setUserFirstName] = useState(firstName);
  const [userLastName, setUserLastName] = useState(lastName);
  const userType = getUserType();
  const nameInitials = useMemo(() => {
    // Get initials of each first name
    const firstNameInitials = userFirstName
      ?.split(' ')
      ?.map((singleWord) => singleWord[0]);

    // Get initials of each last name
    const lastNameInitials = userLastName
      ?.split(' ')
      ?.map((singleWord) => singleWord[0]);

    // Has a first name and last name initial
    if (firstNameInitials && lastNameInitials) {
      return `${firstNameInitials[0]}${lastNameInitials[0]}`;
    }

    // Has two or more first name
    if (firstNameInitials && firstNameInitials.length > 1) {
      return `${firstNameInitials[0]}${firstNameInitials[1]}`;
    }

    // Only has one first name
    if (userFirstName) {
      return `${userFirstName[0]}${userFirstName[1]}`;
    }

    // Doesnt has first name and has two or more last names
    if (lastNameInitials && lastNameInitials.length > 1) {
      return `${lastNameInitials[0]}${lastNameInitials[1]}`;
    }

    // Only has one last name
    if (userLastName) {
      return `${userLastName[0]}${userLastName[1]}`;
    }
    return 'N/A';
  }, [userFirstName, userLastName]);

  const backgroundColor = useMemo(() => {
    switch (userType) {
      case UserTypes.INTERNAL:
        return 'bg-internal-main';
      case UserTypes.EMPLOYER:
        return 'bg-employer-primary';
      case UserTypes.CANDIDATE:
      case UserTypes.GUEST:
      default:
        return 'bg-employer-primary';
    }
  }, [userType]);

  useEffect(() => {
    if (firstName !== undefined) setUserFirstName(firstName);
    if (lastName !== undefined) setUserLastName(lastName);
  }, [firstName, lastName]);

  return (
    <div
      className={`${
        !hideBorder && 'border'
      } w-full aspect-square rounded-full items-center justify-center flex`}
    >
      {picture ? (
        <div
          className={`rounded-full overflow-hidden ${
            addPadding && (typeof addPadding === 'boolean' ? 'p-2' : addPadding)
          } flex flex-row justify-center max-h-full`}
        >
          <div className="rounded-full overflow-hidden items-center justify-center flex">
            <img src={picture} alt={`${userFirstName} ${userLastName}`} />
          </div>
        </div>
      ) : (
        <div
          className={`h-full w-full flex overflow-hidden rounded-full justify-center items-center text-white ${
            boldText && 'font-bold'
          } ${fontSize || 'text-md'} uppercase ${backgroundColor}`}
        >
          {nameInitials.toUpperCase()}
        </div>
      )}
    </div>
  );
};

DefaultIcon2.defaultProps = {
  picture: undefined,
  isEmployer: false,
  isInternal: false,
  isCandidate: false,
  addPadding: false,
  boldText: false,
  fontSize: '',
  hideBorder: false,
};

export default DefaultIcon2;
